import { Game } from './core/Game';
import './ui/garden-journal.css';
import { Environment } from './environment/Environment';
import { PlantRenderer } from './rendering/PlantRenderer';
import { EnvironmentRenderer } from './rendering/EnvironmentRenderer';
import { InputHandler } from './interaction/InputHandler';
import { UIManager } from './ui/UIManager';
import { DebugPanel } from './debug/DebugPanel';
import { CurrencyManager } from './economy/CurrencyManager';
import { PlantManager } from './managers/PlantManager';
import { GardenManager } from './managers/GardenManager';
import { EventManager } from './managers/EventManager';
import { eventBus } from './core/EventBus';
import { iconLeaf } from './icons';

class Plotlet {
  private game: Game;
  private environment: Environment;
  private plantRenderer: PlantRenderer;
  private envRenderer: EnvironmentRenderer;
  private inputHandler: InputHandler;
  private uiManager: UIManager;
  private currencyManager: CurrencyManager;

  private plantManager: PlantManager;
  private gardenManager: GardenManager;

  private canvas: HTMLCanvasElement;
  private lastRenderGameTime: number = 0;

  constructor() {
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    const uiLayer = document.getElementById('ui-layer') as HTMLElement;
    this.canvas = canvas;

    // 初始化核心组件
    this.game = new Game(canvas);
    this.environment = new Environment();
    this.plantRenderer = new PlantRenderer(canvas.getContext('2d')!);
    this.envRenderer = new EnvironmentRenderer(canvas.getContext('2d')!);
    this.currencyManager = new CurrencyManager();
    this.inputHandler = new InputHandler(canvas, (x, y) => this.uiManager.onCanvasMouseMove(x, y));

    // 初始化管理器（UIManager 依赖 PlantManager，PlantManager 依赖 UIManager，需要先创建 PlantManager）
    this.plantManager = new PlantManager(
      canvas,
      null as any, // uiManager 稍后设置
      this.inputHandler,
      this.plantRenderer,
      this.currencyManager
    );

    this.uiManager = new UIManager(uiLayer, this.game, canvas, () => this.plantManager.getPlants(), this.currencyManager);

    // 回填 uiManager 和 plantManager 引用
    this.plantManager.setUIManager(this.uiManager);
    this.uiManager.setPlantManager(this.plantManager);

    this.envRenderer.setSize(canvas.width, canvas.height);

    this.gardenManager = new GardenManager(
      this.game,
      this.environment,
      this.plantManager,
      this.uiManager,
      this.currencyManager,
      uiLayer
    );

    // EventManager 在构造时注册事件处理器，无需保留引用
    new EventManager(this.game);

    // 初始化应用
    this.gardenManager.initializeGarden();

    const firstPlant = this.plantManager.getPlants()[0] || null;
    new DebugPanel(firstPlant, this.game, this.environment);

    this.setupRenderLoop();
    this.setupEventListeners();
    this.game.start();

    this.uiManager.showNotification(`${iconLeaf({ size: 18 })} 欢迎来到 Plotlet`, 3000);
  }

  private setupEventListeners(): void {
    eventBus.on('plant:watered', (plant: any) => {
      const rect = this.canvas.getBoundingClientRect();
      const px = plant.position.x * rect.width;
      const py = plant.position.y * rect.height;
      this.envRenderer.addWetSpot(px, py);
    });
  }

  private setupRenderLoop(): void {
    eventBus.on('game:render', (_ctx: CanvasRenderingContext2D, width: number, height: number, time: number) => {
      this.environment.updateDisplay();

      const plants = this.plantManager.getPlants();
      for (const plant of plants) {
        plant.updateDisplay();
      }

      this.envRenderer.setSize(width, height);

      this.envRenderer.setPlants(plants.map(p => ({ x: p.position.x, y: p.position.y })));
      this.envRenderer.setGrowLightPlants(
        plants
          .filter(p => p.supplementalLight > 0)
          .map(p => ({ x: p.position.x, y: p.position.y, intensity: p.supplementalLight / 100 }))
      );

      const mousePos = this.inputHandler.getLastMousePos();
      if (mousePos) {
        this.envRenderer.setMousePos(mousePos.x, mousePos.y);
      } else {
        this.envRenderer.setMousePos(-1, -1);
      }

      const deltaTime = time - this.lastRenderGameTime;
      this.lastRenderGameTime = time;
      this.envRenderer.render(this.environment, time, deltaTime);

      // 种植模式：显示空闲格子提示
      if (this.inputHandler.isPlacing()) {
        const slots = this.plantManager.getEmptyGridSlots();

        let hoverSlot: { x: number; y: number } | null = null;
        const hoverMousePos = this.inputHandler.getLastMousePos();
        if (hoverMousePos) {
          let nearestDist = Infinity;
          for (const slot of slots) {
            const sx = slot.x * width;
            const sy = slot.y * height;
            const dist = Math.sqrt((hoverMousePos.x - sx) ** 2 + (hoverMousePos.y - sy) ** 2);
            if (dist < nearestDist && dist < 45) {
              nearestDist = dist;
              hoverSlot = slot;
            }
          }
        }

        this.plantRenderer.renderGridHints(slots, width, height, hoverSlot);
      }

      // 按Y排序绘制，远的先画
      const sortedPlants = [...plants].sort((a, b) => a.position.y - b.position.y);
      for (const plant of sortedPlants) {
        const px = plant.position.x * width;
        const py = plant.position.y * height;
        this.plantRenderer.render(plant, px, py, height, time);
      }

      // 统一的补光效果（覆盖在植物上方）
      this.envRenderer.renderGrowLights(time);

      // 从开花植物飘落花瓣
      const bloomingPlants = plants.filter(p => p.getCurrentStage()?.id === 'blooming');
      if (bloomingPlants.length > 0 && Math.random() < 0.006 * bloomingPlants.length) {
        const source = bloomingPlants[Math.floor(Math.random() * bloomingPlants.length)];
        const px = source.position.x * width;
        const py = source.position.y * height;
        this.envRenderer.spawnPetal(px, py);
      }

      this.renderConnectionLines(_ctx, width, height);
    });

    eventBus.on('game:update', (delta: number, _gameTime: number) => {
      const temp = this.environment.temperature;
      for (const plant of this.plantManager.getPlants()) {
        plant.temperature = temp;
        if (plant.supplementalLight > 0) {
          const lightCost = this.currencyManager.getLightCost(plant.supplementalLight, delta);
          if (!this.currencyManager.spend(lightCost)) {
            plant.setSupplementalLight(0);
          }
        }
        // 盛花阶段被动收益
        if (plant.currentStageIndex === plant.species.stages.length - 1 && plant.species.bloomingIncomeRate > 0) {
          const income = plant.species.bloomingIncomeRate * (plant.health / 100) * delta;
          this.currencyManager.earn(income);
        }
      }
    });
  }

  private renderConnectionLines(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const infoPanels = this.gardenManager.getInfoPanels();
    infoPanels.forEach((panel, plantId) => {
      const plant = this.plantManager.getPlantById(plantId);
      if (!plant) return;

      const px = plant.position.x * width;
      const py = plant.position.y * height;

      const panelRect = panel.getScreenRect();
      const canvasRect = this.canvas.getBoundingClientRect();
      const scaleX = width / canvasRect.width;
      const scaleY = height / canvasRect.height;

      const panelCenterX = (panelRect.left + panelRect.width / 2 - canvasRect.left) * scaleX;
      const panelCenterY = (panelRect.top + panelRect.height / 2 - canvasRect.top) * scaleY;

      const plantTopX = px;
      const plantTopY = py - 140;

      ctx.save();
      ctx.strokeStyle = 'rgba(180, 160, 140, 0.4)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(plantTopX, plantTopY);
      ctx.lineTo(panelCenterX, panelCenterY);
      ctx.stroke();

      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(180, 160, 140, 0.5)';
      ctx.beginPath();
      ctx.arc(panelCenterX, panelCenterY, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Plotlet();
});
